/**
 * Public Vacation Rental Property Detail Page
 * Estate Flow - Sebastian Alliance Group LLC
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  MapPin, 
  Calendar, 
  Users, 
  Star, 
  Heart,
  Share2,
  Wifi,
  Car,
  Waves,
  Utensils,
  Tv,
  Wind,
  Home,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const amenityDetails = {
  'wifi': { icon: Wifi, label: 'Free WiFi' },
  'parking': { icon: Car, label: 'Free Parking' },
  'pool': { icon: Waves, label: 'Swimming Pool' },
  'kitchen': { icon: Utensils, label: 'Full Kitchen' },
  'tv': { icon: Tv, label: 'Smart TV' },
  'ac': { icon: Wind, label: 'Air Conditioning' },
};

const VacationPropertyDetail = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: 1,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: ''
  });

  useEffect(() => {
    loadProperty();
  }, [propertyId]);

  const loadProperty = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/public/vacation-rentals/properties/${propertyId}`);
      setProperty(response.data.property);
    } catch (error) {
      console.error('Error loading property:', error);
      // Load sample property for demo
      setProperty(getSampleProperty());
    } finally {
      setLoading(false);
    }
  };

  const calculatePricing = async () => {
    if (!bookingData.checkIn || !bookingData.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    
    setPricingLoading(true);
    try {
      const response = await axios.get(
        `${API}/public/vacation-rentals/properties/${propertyId}/pricing?check_in=${bookingData.checkIn}&check_out=${bookingData.checkOut}&guests=${bookingData.guests}`
      );
      setPricing(response.data.pricing);
      setShowBookingForm(true);
    } catch (error) {
      console.error('Error calculating pricing:', error);
      // Calculate locally for demo
      const nights = Math.ceil((new Date(bookingData.checkOut) - new Date(bookingData.checkIn)) / (1000 * 60 * 60 * 24));
      const subtotal = property.base_nightly_rate * nights;
      const serviceFee = subtotal * 0.02;
      const discount = subtotal * 0.10;
      setPricing({
        nights,
        nightly_rate: property.base_nightly_rate,
        subtotal,
        cleaning_fee: property.cleaning_fee || 0,
        service_fee: serviceFee,
        direct_booking_discount: discount,
        total: subtotal + (property.cleaning_fee || 0) + serviceFee - discount,
        savings_vs_ota: subtotal * 0.13
      });
      setShowBookingForm(true);
    } finally {
      setPricingLoading(false);
    }
  };

  const handleBooking = async () => {
    if (!bookingData.firstName || !bookingData.lastName || !bookingData.email || !bookingData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/public/vacation-rentals/bookings`, {
        property_id: propertyId,
        check_in: bookingData.checkIn,
        check_out: bookingData.checkOut,
        guests: bookingData.guests,
        guest_first_name: bookingData.firstName,
        guest_last_name: bookingData.lastName,
        guest_email: bookingData.email,
        guest_phone: bookingData.phone,
        special_requests: bookingData.specialRequests
      });
      
      toast.success('Booking created! Proceeding to payment...');
      navigate(`/vacation-rentals/booking/${response.data.booking.id}/payment`);
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
    }
  };

  const getSampleProperty = () => ({
    id: propertyId,
    name: 'Oceanfront Paradise Villa',
    property_type: 'villa',
    description: 'Experience luxury beachfront living in this stunning 4-bedroom villa. Wake up to panoramic ocean views, relax by your private infinity pool, and enjoy the gentle sea breeze from multiple terraces. This fully equipped home features a gourmet kitchen, spacious living areas, and direct beach access. Perfect for families or groups seeking an unforgettable tropical getaway.',
    address: '123 Ocean Drive',
    city: 'Miami Beach',
    country: 'USA',
    bedrooms: 4,
    bathrooms: 3,
    max_guests: 8,
    square_feet: 3500,
    base_nightly_rate: 450,
    cleaning_fee: 150,
    service_fee_percent: 2,
    direct_booking_discount: 10,
    rating: 4.9,
    review_count: 127,
    min_nights: 2,
    cover_photo: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200',
    photos: [
      'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200',
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200',
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200'
    ],
    amenities: ['wifi', 'pool', 'parking', 'kitchen', 'tv', 'ac'],
    host: { first_name: 'Sarah', verified: true },
    reviews: [
      { guest_name: 'John D.', rating: 5, comment: 'Amazing property! The views were incredible and the villa had everything we needed.', created_at: '2024-12-15' },
      { guest_name: 'Maria S.', rating: 5, comment: 'Perfect for our family vacation. Kids loved the pool!', created_at: '2024-12-01' }
    ]
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Home className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Property not found</h2>
        <Button onClick={() => navigate('/vacation-rentals/search')}>Back to Search</Button>
      </div>
    );
  }

  const photos = property.photos?.length > 0 ? property.photos : [property.cover_photo];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/vacation-rentals/search')}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setIsFavorite(!isFavorite)}>
                <Heart className={`h-4 w-4 mr-1 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                Save
              </Button>
              <Button variant="ghost" size="sm">
                <Share2 className="h-4 w-4 mr-1" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Photo Gallery */}
      <div className="relative bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="relative h-[400px] md:h-[500px]">
            <img
              src={photos[currentPhotoIndex]}
              alt={property.name}
              className="w-full h-full object-cover"
            />
            
            {photos.length > 1 && (
              <>
                <button
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white"
                  onClick={() => setCurrentPhotoIndex((currentPhotoIndex - 1 + photos.length) % photos.length)}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white"
                  onClick={() => setCurrentPhotoIndex((currentPhotoIndex + 1) % photos.length)}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {photos.map((_, index) => (
                    <button
                      key={index}
                      className={`w-2 h-2 rounded-full ${index === currentPhotoIndex ? 'bg-white' : 'bg-white/50'}`}
                      onClick={() => setCurrentPhotoIndex(index)}
                    />
                  ))}
                </div>
              </>
            )}
            
            <Badge className="absolute top-4 left-4 bg-emerald-600">
              Save {property.direct_booking_discount}% - Book Direct
            </Badge>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title Section */}
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{property.name}</h1>
                  <p className="text-gray-500 flex items-center gap-1 mt-1">
                    <MapPin className="h-4 w-4" />
                    {property.address}, {property.city}, {property.country}
                  </p>
                </div>
                {property.rating > 0 && (
                  <div className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-lg">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-bold">{property.rating}</span>
                    <span className="text-gray-500">({property.review_count} reviews)</span>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-4 mt-4 text-gray-600">
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {property.max_guests} guests
                </span>
                <span className="flex items-center gap-1">
                  <Bed className="h-4 w-4" />
                  {property.bedrooms} bedrooms
                </span>
                <span className="flex items-center gap-1">
                  <Bath className="h-4 w-4" />
                  {property.bathrooms} bathrooms
                </span>
                {property.square_feet && (
                  <span className="flex items-center gap-1">
                    <Home className="h-4 w-4" />
                    {property.square_feet.toLocaleString()} sq ft
                  </span>
                )}
              </div>
            </div>

            <Separator />

            {/* Host */}
            {property.host && (
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-emerald-600">{property.host.first_name?.[0]}</span>
                </div>
                <div>
                  <p className="font-semibold">Hosted by {property.host.first_name}</p>
                  {property.host.verified && (
                    <p className="text-sm text-emerald-600 flex items-center gap-1">
                      <Shield className="h-4 w-4" />
                      Verified Host
                    </p>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* Description */}
            <div>
              <h2 className="text-xl font-semibold mb-4">About this place</h2>
              <p className="text-gray-600 whitespace-pre-line">{property.description}</p>
            </div>

            <Separator />

            {/* Amenities */}
            <div>
              <h2 className="text-xl font-semibold mb-4">What this place offers</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {property.amenities?.map(amenity => {
                  const detail = amenityDetails[amenity] || { icon: Check, label: amenity };
                  const Icon = detail.icon;
                  return (
                    <div key={amenity} className="flex items-center gap-3 text-gray-600">
                      <Icon className="h-5 w-5" />
                      <span>{detail.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* Reviews */}
            {property.reviews?.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-4">
                  <Star className="h-5 w-5 inline fill-yellow-400 text-yellow-400 mr-1" />
                  {property.rating} · {property.review_count} reviews
                </h2>
                <div className="space-y-4">
                  {property.reviews.map((review, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">{review.guest_name}</span>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span>{review.rating}</span>
                          </div>
                        </div>
                        <p className="text-gray-600">{review.comment}</p>
                        <p className="text-sm text-gray-400 mt-2">{review.created_at}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <div className="flex items-end justify-between">
                  <div>
                    <span className="text-2xl font-bold">${property.base_nightly_rate}</span>
                    <span className="text-gray-500"> / night</span>
                  </div>
                  <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                    {property.direct_booking_discount}% off - Book Direct
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-gray-500">CHECK-IN</Label>
                    <Input
                      type="date"
                      value={bookingData.checkIn}
                      onChange={(e) => setBookingData({...bookingData, checkIn: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">CHECK-OUT</Label>
                    <Input
                      type="date"
                      value={bookingData.checkOut}
                      onChange={(e) => setBookingData({...bookingData, checkOut: e.target.value})}
                      min={bookingData.checkIn || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs text-gray-500">GUESTS</Label>
                  <Input
                    type="number"
                    min={1}
                    max={property.max_guests}
                    value={bookingData.guests}
                    onChange={(e) => setBookingData({...bookingData, guests: parseInt(e.target.value)})}
                  />
                </div>

                {!showBookingForm && (
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={calculatePricing}
                    disabled={pricingLoading}
                  >
                    {pricingLoading ? 'Calculating...' : 'Check Availability'}
                  </Button>
                )}

                {showBookingForm && pricing && (
                  <>
                    <Separator />
                    
                    {/* Pricing Breakdown */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>${pricing.nightly_rate} × {pricing.nights} nights</span>
                        <span>${pricing.subtotal}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Cleaning fee</span>
                        <span>${pricing.cleaning_fee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Service fee (2%)</span>
                        <span>${pricing.service_fee?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-emerald-600">
                        <span>Direct booking discount</span>
                        <span>-${pricing.direct_booking_discount?.toFixed(2)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span>${pricing.total?.toFixed(2)}</span>
                      </div>
                      <div className="bg-emerald-50 p-3 rounded-lg text-center">
                        <p className="text-emerald-700 font-medium">
                          💰 You save ~${pricing.savings_vs_ota?.toFixed(2)} vs OTAs!
                        </p>
                      </div>
                    </div>

                    <Separator />

                    {/* Guest Details */}
                    <div className="space-y-3">
                      <h3 className="font-semibold">Guest Details</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="First name *"
                          value={bookingData.firstName}
                          onChange={(e) => setBookingData({...bookingData, firstName: e.target.value})}
                        />
                        <Input
                          placeholder="Last name *"
                          value={bookingData.lastName}
                          onChange={(e) => setBookingData({...bookingData, lastName: e.target.value})}
                        />
                      </div>
                      <Input
                        type="email"
                        placeholder="Email *"
                        value={bookingData.email}
                        onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                      />
                      <Input
                        type="tel"
                        placeholder="Phone *"
                        value={bookingData.phone}
                        onChange={(e) => setBookingData({...bookingData, phone: e.target.value})}
                      />
                      <Textarea
                        placeholder="Special requests (optional)"
                        value={bookingData.specialRequests}
                        onChange={(e) => setBookingData({...bookingData, specialRequests: e.target.value})}
                        rows={2}
                      />
                    </div>

                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleBooking}
                    >
                      Reserve - ${pricing.total?.toFixed(2)}
                    </Button>
                    
                    <p className="text-xs text-center text-gray-500">
                      You won't be charged yet
                    </p>
                  </>
                )}

                <div className="text-center text-sm text-gray-500">
                  <p>Minimum stay: {property.min_nights} nights</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VacationPropertyDetail;
